import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import { ChevronLeft, ChevronRight, Send, Save, ArrowLeft, Sparkles } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { formatApiErrorDetail } from '../../context/AuthContext';

import ContactDetailsStep from '../client/steps/ContactDetailsStep';
import PersonalInfoStep from '../client/steps/PersonalInfoStep';
import EmploymentStep from '../client/steps/EmploymentStep';
import InvestmentAccountsStep from '../client/steps/InvestmentAccountsStep';
import OtherAssetsStep from '../client/steps/OtherAssetsStep';
import InsurancePolicyStep from '../client/steps/InsurancePolicyStep';
import LiabilitiesStep from '../client/steps/LiabilitiesStep';
import GoalsStep from '../client/steps/GoalsStep';
import RetirementExpensesStep from '../client/steps/RetirementExpensesStep';
import AnnualTaxesStep from '../client/steps/AnnualTaxesStep';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Attachments + Welcome + Review are intentionally excluded from advisor pre-fill.
const STEPS = [
  { key: 'contact',            title: 'Contact Details',                     section: 'contact',            Component: ContactDetailsStep },
  { key: 'personal',           title: 'Personal Information',                section: 'personal',           Component: PersonalInfoStep },
  { key: 'employment',         title: 'Employment & Income',                 section: 'employment',         Component: EmploymentStep },
  { key: 'investments',        title: 'Investment Accounts',                 section: 'investments',        Component: InvestmentAccountsStep },
  { key: 'otherAssets',        title: 'Other Assets',                        section: 'otherAssets',        Component: OtherAssetsStep },
  { key: 'insurance',          title: 'Insurance Policy',                    section: 'insurance',          Component: InsurancePolicyStep },
  { key: 'liabilitiesSection', title: 'Liabilities',                         section: 'liabilitiesSection', Component: LiabilitiesStep },
  { key: 'goals',              title: 'Financial Goals',                     section: 'goals',              Component: GoalsStep },
  { key: 'retirement',         title: 'Retirement & Basic Living Expenses',  section: 'retirement',         Component: RetirementExpensesStep },
  { key: 'taxes',              title: 'Annual Taxes',                        section: 'taxes',              Component: AnnualTaxesStep },
];

export default function AdvisorPrefillForm() {
  const { inviteId } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [formData, setFormData] = useState({});

  const load = useCallback(async () => {
    try {
      const [inv, pre] = await Promise.all([
        axios.get(`${API}/advisor/invites/${inviteId}`, { withCredentials: true }),
        axios.get(`${API}/advisor/invites/${inviteId}/prefill`, { withCredentials: true }),
      ]);
      setInvite(inv.data);
      setFormData(pre.data.data || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load invite');
      navigate('/advisor/dashboard');
    } finally {
      setLoading(false);
    }
  }, [inviteId, navigate]);

  useEffect(() => { load(); }, [load]);

  const savePrefillDebounced = useCallback(
    debounce(async (data) => {
      setSaving(true);
      try {
        await axios.post(`${API}/advisor/invites/${inviteId}/prefill`, { data }, { withCredentials: true });
      } catch (e) {
        // Silent — next input retries. Log genuine 5xx for debugging.
        console.warn('[prefill.save] transient failure:', e?.response?.status, e?.message);
      } finally {
        setSaving(false);
      }
    }, 1000),
    [inviteId]
  );

  const updateSection = (section) => (data) => {
    setFormData((prev) => {
      const next = { ...prev, [section]: { ...(prev[section] || {}), ...data } };
      savePrefillDebounced(next);
      return next;
    });
  };

  const handleSaveNow = async () => {
    savePrefillDebounced.flush?.();
    try {
      await axios.post(`${API}/advisor/invites/${inviteId}/prefill`, { data: formData }, { withCredentials: true });
      toast.success('Pre-fill saved');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleSendInvite = async () => {
    savePrefillDebounced.flush?.();
    setSending(true);
    try {
      // Ensure latest pre-fill is persisted before triggering the client email
      await axios.post(`${API}/advisor/invites/${inviteId}/prefill`, { data: formData }, { withCredentials: true });
      // Regenerate produces a fresh link + email — reused as the "send invite" action
      const { data } = await axios.post(`${API}/advisor/invites/${inviteId}/regenerate`, {}, { withCredentials: true });
      if (data.email_sent) {
        toast.success(`Invite emailed to ${invite.client_email}`);
      } else {
        const fullLink = data.invite_url || `${window.location.origin}/f/${data.token}`;
        try { await navigator.clipboard.writeText(fullLink); } catch (err) { console.warn("suppressed error:", err); }
        toast.warning(`Email delivery failed. Link copied to clipboard — share it manually.`, { duration: 12000 });
      }
      navigate('/advisor/dashboard');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5b93d6]"></div>
      </div>
    );
  }

  const current = STEPS[stepIdx];
  const StepComponent = current.Component;
  const progress = ((stepIdx + 1) / STEPS.length) * 100;
  const emailAlreadySent = !!invite.email_sent_at;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/advisor/dashboard')}
                className="text-white hover:bg-white/10"
                data-testid="prefill-back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Dashboard
              </Button>
              <div className="hidden sm:block h-6 w-px bg-white/20" />
              <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-8 h-8 rounded-md shadow-sm" data-testid="brand-logo" />
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.03em' }}>
                  <Sparkles className="w-4 h-4 text-[#5b93d6]" />
                  Pre-fill Form
                </h1>
                <p className="text-xs text-white/80 truncate">
                  For <span className="font-medium">{invite.client_name || invite.client_email}</span>
                  {emailAlreadySent && <span className="ml-2 rounded-full bg-white/15 px-2 py-0.5 text-[10px] uppercase tracking-wider">Email sent</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {saving && <span className="text-xs text-white/80">Saving…</span>}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSaveNow}
                className="text-white hover:bg-white/10"
                data-testid="prefill-save-button"
              >
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button
                onClick={handleSendInvite}
                disabled={sending}
                className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white"
                data-testid="prefill-send-invite-button"
              >
                <Send className={`w-4 h-4 mr-2 ${sending ? 'animate-pulse' : ''}`} />
                {sending ? 'Sending…' : emailAlreadySent ? 'Resend Invite' : 'Send Invite to Client'}
              </Button>
            </div>
          </div>
        </div>
        <Progress value={progress} className="h-1 rounded-none" data-testid="prefill-progress-bar" />
      </header>

      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Info banner */}
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" />
          <div className="text-sm text-blue-900">
            <strong>Pre-fill mode.</strong> Fill in any fields you already know for {invite.client_name || 'this client'}.
            The client will see your entries as starting values and can edit anything.
            Attachments and the review/submit steps stay with the client.
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Step navigator */}
          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-[#e2e8f0] bg-white p-4 lg:sticky lg:top-24">
              <h4 className="mb-3 text-sm font-semibold text-[#0f172a]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Steps
              </h4>
              <ol className="space-y-1">
                {STEPS.map((s, i) => {
                  const active = i === stepIdx;
                  const hasData = !!formData[s.section] && Object.keys(formData[s.section] || {}).length > 0;
                  return (
                    <li key={s.key}>
                      <button
                        onClick={() => setStepIdx(i)}
                        className={`w-full rounded-md px-3 py-2 text-left text-sm transition-colors ${
                          active
                            ? 'bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] text-white font-medium'
                            : 'text-[#475569] hover:bg-[#f8fafc]'
                        }`}
                        data-testid={`prefill-step-nav-${i + 1}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-xs ${
                            active ? 'bg-white/20 text-white' : hasData ? 'bg-green-100 text-green-700' : 'bg-[#e2e8f0] text-[#475569]'
                          }`}>{i + 1}</span>
                          <span className="truncate">{s.title}</span>
                          {hasData && !active && <span className="ml-auto text-[10px] text-green-600">●</span>}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </div>
          </aside>

          {/* Step content */}
          <div className="lg:col-span-3">
            <div className="rounded-lg border border-[#e2e8f0] bg-white p-8" data-testid="prefill-step-content">
              <StepComponent
                formData={formData[current.section] || {}}
                updateFormData={updateSection(current.section)}
              />
            </div>
            <div className="mt-6 flex items-center justify-between">
              <Button
                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                disabled={stepIdx === 0}
                variant="outline"
                data-testid="prefill-back-button"
              >
                <ChevronLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <span className="text-sm text-[#475569]" data-testid="prefill-step-indicator">
                Step {stepIdx + 1} of {STEPS.length}
              </span>
              <Button
                onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
                disabled={stepIdx === STEPS.length - 1}
                className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white"
                data-testid="prefill-next-button"
              >
                Next <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
